/**
 * api.ts
 *
 * Central API utility for Phase 0b.
 *
 * What it does:
 *  - Sends cookies on every request (credentials: "include")
 *  - Reads csrf_token cookie, attaches as X-CSRF-Token header on POST/PATCH/PUT/DELETE
 *  - On 401, attempts one silent /auth/refresh, swaps the access token, retries
 *  - Dedupes concurrent refresh attempts (race condition fix for multi-tab / parallel requests)
 *  - Exposes typed auth helpers: api.auth.login, .logout, .elevate, .getSessions, etc.
 *
 * Usage:
 *   import { api } from "../api";
 *   const data = await api.get("/transactions/user/1");
 *   const result = await api.post("/budget/state/1", { monthly_budget: 3000 });
 *   await api.auth.login(email, password);
 *   await api.auth.logout();
 */

// REACT_APP_SAME_ORIGIN=1 (set by the all-in-one build for single-service firm
// instances) makes every call relative to the current origin, since FastAPI
// serves the app + API from the same host. Otherwise use the explicit backend
// URL (Vercel→Railway), falling back to localhost in dev.
export const BASE_URL =
  process.env.REACT_APP_SAME_ORIGIN === "1"
    ? ""
    : (process.env.REACT_APP_API_URL || "http://localhost:8000");


// ─── Token / session storage ──────────────────────────────────────────────────
// Cookie-auth migration: the access token now lives in the HttpOnly
// `mp_access` cookie set by /auth/login + /auth/mfa/verify + /auth/refresh
// + /auth/elevate. We intentionally STOP storing it in localStorage so a
// stored XSS payload can't read it. The session_user object stays in
// localStorage because it's display-only (name, role, email-for-greeting)
// and an attacker who could read localStorage can already read the DOM.
// Bearer fallback for the cross-origin Vercel→Railway deploy. Chrome's
// 2024-2025 third-party cookie phase-out drops cross-site cookies even
// with SameSite=None+Secure+Partitioned in some configurations, so we
// can't rely on mp_access surviving. Backend accepts EITHER an
// Authorization: Bearer header OR the cookie (auth_deps.get_current_user).
// We send both: cookie for browsers that honor it (best — HttpOnly,
// XSS-resistant), Bearer for everything else.
//
// Storage choice: sessionStorage, not localStorage. sessionStorage is
// cleared on tab close → smaller XSS exposure window. Trade-off: each
// new tab requires a re-login, which is acceptable for an advisor app.
const ACCESS_KEY = "mp_access_token";

function getToken(): string | null {
  try { return sessionStorage.getItem(ACCESS_KEY); } catch { return null; }
}

/** The access token, for the few raw-`fetch` call sites that can't use the
 * `api.*` helpers (blob downloads that need the Response object).
 *
 * Exported because four such sites were reading `localStorage["mp_token"]` —
 * a key nothing has written since the token moved to sessionStorage. They
 * silently sent no Authorization header and worked only where the cookie
 * survives, i.e. not on the cross-origin Vercel→Railway deploy. Use this
 * rather than reaching into storage by name. */
export function getAccessToken(): string | null {
  return getToken();
}

function setToken(token: string): void {
  if (!token) return;
  try { sessionStorage.setItem(ACCESS_KEY, token); } catch { /* swallow */ }
}

export function saveSession(token: string, user: any) {
  // User object: localStorage (display fields only — name, role, email).
  // Access token: sessionStorage (Bearer fallback for cookies that don't
  // make it cross-origin). Token in sessionStorage is XSS-readable but
  // sessionStorage clears on tab close so the exposure window is short.
  localStorage.setItem("mp_user", JSON.stringify(user));
  if (token) setToken(token);
  markSessionEstablished();  // re-arm the session-expired signal for this session
}

export function clearSession() {
  localStorage.removeItem("mp_token");
  localStorage.removeItem("mp_user");
  try { sessionStorage.removeItem("mp_csrf"); } catch { /* swallow */ }
  try { sessionStorage.removeItem(ACCESS_KEY); } catch { /* swallow */ }
  // Which account a client-who-is-also-a-POA chose (components/ActingAs.tsx).
  try { sessionStorage.removeItem("mp_acting_for"); } catch { /* swallow */ }
  // Scrub the non-HttpOnly csrf_token cookie client-side too. The backend
  // /auth/logout sends Set-Cookie expirations, but Chrome's cross-origin
  // cookie matching can mis-handle the deletion if the original cookie's
  // SameSite/Secure/Partitioned attributes don't match exactly. Belt +
  // suspenders: nuke it from JS as well so reload doesn't see a stale
  // CSRF token + assume there's still a session. (refresh_token and
  // mp_access are HttpOnly so JS can't touch them — those rely entirely
  // on backend Set-Cookie deletion.)
  try {
    document.cookie = "csrf_token=; Max-Age=0; Path=/; SameSite=Lax";
    document.cookie = "csrf_token=; Max-Age=0; Path=/; SameSite=None; Secure";
  } catch { /* swallow */ }
}

export function getStoredUser(): any | null {
  try {
    const u = localStorage.getItem("mp_user");
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
}

// ─── Session-expired signal ─────────────────────────────────────────────────
// When the access token can't be silently refreshed mid-session, we clear
// local state and broadcast a window event so the app shell can show a
// dedicated "you've been signed out" page instead of leaving the user on a
// dead page whose data quietly 401s. Mirrors the ap:mfa-enrollment-required
// pattern. Deliberately NO window.location navigation — a hard reload here is
// what caused the old infinite-loop (restoreSession re-runs on "/" load).
let _sessionExpiredNotified = false;

// Reset once a real session is (re)established so a later expiry re-fires.
export function markSessionEstablished(): void {
  _sessionExpiredNotified = false;
}

export function notifySessionExpired(): void {
  // Only signal if a session actually existed — otherwise a first-load 401
  // (no prior login) would pop a scary "session expired" screen instead of
  // the normal Login. Check BEFORE clearSession scrubs the stored user.
  const hadSession = !!getStoredUser() || !!getToken();
  clearSession();
  if (!hadSession || _sessionExpiredNotified) return;
  _sessionExpiredNotified = true;
  try { window.dispatchEvent(new CustomEvent("ap:session-expired")); } catch { /* SSR / no window */ }
}


// ─── CSRF token ───────────────────────────────────────────────────────────────
// Primary: read from the csrf_token cookie (works in same-site dev where
// frontend + backend share localhost). Fallback: sessionStorage, populated
// by saveCsrfToken below — needed in production where frontend (Vercel)
// and backend (Railway) are on different origins and document.cookie can
// only see cookies for the current origin.
function getCsrfToken(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  if (match) return decodeURIComponent(match[1]);
  try {
    return sessionStorage.getItem("mp_csrf") || null;
  } catch {
    return null;
  }
}

function saveCsrfToken(token: string | undefined | null): void {
  if (!token) return;
  try { sessionStorage.setItem("mp_csrf", token); } catch { /* swallow */ }
}


// ─── Refresh dedup ────────────────────────────────────────────────────────────
// When N parallel requests all 401 at once, we want exactly ONE /auth/refresh
// call to fire. All of them await the same promise. This prevents reuse-detection
// false positives (we'd be replaying our own refresh token N times otherwise).
let inFlightRefresh: Promise<boolean> | null = null;

export async function refreshAccessToken(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  inFlightRefresh = (async () => {
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`${BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (data?.access_token) {
        setToken(data.access_token);
        saveCsrfToken(data?.csrf_token);
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      // Clear after a microtask so all current awaiters get the result first
      setTimeout(() => { inFlightRefresh = null; }, 0);
    }
  })();

  return inFlightRefresh;
}


// ─── Core request function ────────────────────────────────────────────────────
const STATE_CHANGING = new Set(["POST", "PATCH", "PUT", "DELETE"]);

/**
 * Per-call options. Today only `signal`, so a read can be cancelled when its
 * answer stops mattering (the panel unmounted, or the user switched client and
 * a slower earlier response would otherwise land on top of the newer one).
 * See hooks/useFetch.ts, which is the way callers should normally get this.
 */
export interface RequestOptions {
  signal?: AbortSignal;
}

/** True for the DOMException fetch throws on abort — not a real failure. */
export function isAbortError(e: unknown): boolean {
  return !!e && typeof e === "object" && (e as any).name === "AbortError";
}

async function rawRequest(
  method: string,
  path: string,
  body?: any,
  isFormData = false,
  opts?: RequestOptions,
): Promise<Response> {
  // Authorization is carried by the HttpOnly mp_access cookie via
  // credentials:"include" — no Bearer header needed. getToken() returns
  // null after the cookie-auth migration; left in place so any external
  // caller (browser extension, curl test) can still set it explicitly.
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body && !isFormData) headers["Content-Type"] = "application/json";

  if (STATE_CHANGING.has(method)) {
    let csrf = getCsrfToken();
    // Pre-emptive seed: cross-origin (Vercel → Railway) deployments
    // can't read the csrf_token cookie from document.cookie, and a
    // freshly-opened tab has empty sessionStorage. If we have neither,
    // bounce off /auth/refresh once to populate sessionStorage before
    // sending the request — saves a 403 round-trip and avoids the
    // "CSRF token missing" UX dead-end if the retry path itself fails
    // for some reason.
    if (!csrf && path !== "/auth/refresh") {
      await refreshAccessToken();
      csrf = getCsrfToken();
    }
    if (csrf) headers["X-CSRF-Token"] = csrf;
  }

  // Transient-failure retry. Railway free-tier pods sleep after idle
  // and the first request after wake-up sometimes returns
  // TypeError("Failed to fetch") before the pod is reachable. Retry
  // once after 600ms — long enough for the pod to come up, short
  // enough that the user just sees a brief stall instead of a
  // confusing error.
  //
  // Skip the auto-retry for upload routes: re-sending a 50MB body
  // after 600ms wastes the user's bandwidth and doubles their wait.
  // Those failures fall through to the friendly error directly so
  // the user can decide to retry themselves.
  const isUploadPath = (
    path.startsWith("/documents/upload") ||
    path.startsWith("/documents/bulk-upload") ||
    path.includes("/plan-document") ||
    path.includes("/documents/upload") ||
    path.startsWith("/prospect/documents/upload") ||
    path.startsWith("/firm/receipts") ||
    path.startsWith("/compliance/deliveries")
  );

  try {
    return await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      credentials: "include",
      // Never serve API responses from the browser HTTP cache — a live
      // financial app must not show stale reads. Without this, a GET re-issued
      // after an edit (e.g. reopening the IPS editor) returned the cached
      // pre-edit body, so edits "didn't show" until a hard refresh.
      cache: "no-store",
      body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
      signal: opts?.signal,
    });
  } catch (e: any) {
    // An abort is the caller saying it stopped caring. Never retry it, and
    // never dress it up as a connection failure — useFetch swallows it.
    if (isAbortError(e)) throw e;
    const isFetchError = e instanceof TypeError && String(e?.message || "").toLowerCase().includes("fetch");
    if (!isFetchError) throw e;
    if (isUploadPath) {
      const friendly: any = new Error("Upload didn't complete — check your connection and try again. The file's still on your computer; nothing was saved.");
      friendly.original = e;
      throw friendly;
    }
    await new Promise(r => setTimeout(r, 600));
    try {
      return await fetch(`${BASE_URL}${path}`, {
        method,
        headers,
        credentials: "include",
        cache: "no-store",
        body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
        signal: opts?.signal,
      });
    } catch (retry: any) {
      if (isAbortError(retry)) throw retry;
      // Re-throw with a clearer message so any alert/toast that just
      // surfaces e.message tells the user what's going on instead of
      // the cryptic "Failed to fetch".
      const friendly: any = new Error("Couldn't reach the server. Check your connection and try again in a moment.");
      friendly.original = retry;
      throw friendly;
    }
  }
}

async function request(
  method: string,
  path: string,
  body?: any,
  isFormData = false,
  opts?: RequestOptions,
): Promise<any> {
  let res = await rawRequest(method, path, body, isFormData, opts);

  // 401: attempt one silent refresh, then retry
  if (res.status === 401 && path !== "/auth/refresh" && path !== "/auth/login") {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      res = await rawRequest(method, path, body, isFormData, opts);
    } else {
      // refresh failed; clear local state and signal the app shell to show
      // the secure "signed out" page. notifySessionExpired() broadcasts an
      // event (no hard navigation — that caused the old reload loop) and is
      // a no-op at boot (no prior session), so restoreSession() still falls
      // through to the normal Login screen.
      notifySessionExpired();
      throw new Error("Session expired. Please log in again.");
    }
  }

  // Step-up auth: 403 with detail="elevation_required" → prompt for password, retry once
  if (res.status === 403) {
    const errBody = await res.clone().json().catch(() => ({} as any));
    if (errBody?.detail === "elevation_required") {
      try {
        const { promptStepUp } = await import("./components/StepUpModal");
        await promptStepUp();
        res = await rawRequest(method, path, body, isFormData, opts);
      } catch {
        // User cancelled — fall through to the normal error path below
      }
    } else if (errBody?.detail === "CSRF token missing" && path !== "/auth/refresh") {
      // Cross-origin (Vercel→Railway) sometimes drops the csrf cookie + the
      // sessionStorage fallback hasn't been seeded yet (long-lived tab where
      // login predated the saveCsrfToken wiring, or sessionStorage cleared).
      // /auth/refresh re-issues the cookie AND returns csrf_token in the
      // response body so saveCsrfToken can repopulate sessionStorage.
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        res = await rawRequest(method, path, body, isFormData, opts);
      } else {
        // Refresh failed — third-party cookies blocked, refresh token
        // expired, or session truly invalid. The user can't recover
        // from "CSRF token missing" without re-authenticating; signal the
        // secure signed-out page instead of leaving them stuck on a bare
        // "CSRF token missing" alert.
        notifySessionExpired();
        const e: any = new Error("Your session expired. Please sign in again.");
        e.status = 401;
        e.detail = "session_expired";
        throw e;
      }
    } else if (errBody?.detail === "mfa_enrollment_required") {
      // The user must enroll MFA before they can access anything else.
      // Broadcast a window event; App.tsx (or wherever) listens and renders
      // the forced enrollment screen. Throw a typed error so callers don't
      // try to keep going.
      try { window.dispatchEvent(new CustomEvent("ap:mfa-enrollment-required")); } catch {}
      const e: any = new Error("MFA enrollment required");
      e.status = 403;
      e.detail = "mfa_enrollment_required";
      throw e;
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Request failed" }));
    // `err.detail` can be (a) a string ("Not authorized"), (b) a
    // FastAPI 422 validation array ([{loc, msg, type}, …]), or
    // (c) an arbitrary object. Coerce all three into a useful string
    // so the UI never renders "[object Object]" in a toast.
    let message: string;
    if (typeof err.detail === "string") {
      message = err.detail;
    } else if (Array.isArray(err.detail)) {
      // FastAPI ValidationError shape — list of {loc, msg, type}.
      message = err.detail
        .map((d: any) => {
          if (typeof d === "string") return d;
          const loc = Array.isArray(d?.loc) ? d.loc.filter((p: any) => p !== "body").join(".") : "";
          return loc ? `${loc}: ${d?.msg || "invalid"}` : (d?.msg || "invalid");
        })
        .filter(Boolean)
        .join("; ") || "Validation failed";
    } else if (err.detail && typeof err.detail === "object") {
      message = err.detail.msg || JSON.stringify(err.detail);
    } else {
      message = "Request failed";
    }
    // CSRFMiddleware rejection — usually means the production
    // ALLOWED_ORIGINS env var is missing the deploy's domain.
    // Surface a clear nudge instead of the bare server detail so
    // whoever's debugging knows where to look.
    if (res.status === 403 && err.detail === "Origin not allowed") {
      message = `This deploy's origin (${window.location.origin}) is not on the backend allow-list. Add it to ALLOWED_ORIGINS on the API server.`;
    }
    const e: any = new Error(message);
    e.status = res.status;
    e.detail = err.detail;
    throw e;
  }

  // Empty responses (e.g., 204)
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}


// ─── Auth namespace ───────────────────────────────────────────────────────────
const auth = {
  async login(email: string, password: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Login failed" }));
      const e: any = new Error(err.detail || "Login failed");
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    // If MFA is required, the response is {needs_mfa: true, mfa_token: "..."}
    // and contains no access_token/user — caller is responsible for prompting.
    if (data.needs_mfa) {
      return data;
    }
    saveSession(data.access_token, data.user);
    saveCsrfToken(data?.csrf_token);
    return data;
  },

  // Login-flow: text the SMS-MFA code to the pending user. Authed by the
  // mfa_token (no session yet), CSRF-exempt server-side. Returns {sent, phone_last4}.
  async mfaSmsSend(mfaToken: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/mfa/sms/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ mfa_token: mfaToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Couldn't send code" }));
      const e: any = new Error(err.detail || "Couldn't send code");
      e.status = res.status;
      throw e;
    }
    return res.json();
  },

  // Login-flow: email a code to the pending user. Authed by the mfa_token (no
  // session yet), CSRF-exempt server-side. Universal fallback — works even
  // when the account never explicitly enrolled email codes (see mfa.py's
  // mfa_email_send docstring). Returns {sent}.
  async mfaEmailSend(mfaToken: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/mfa/email/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ mfa_token: mfaToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Couldn't send code" }));
      const e: any = new Error(err.detail || "Couldn't send code");
      e.status = res.status;
      throw e;
    }
    return res.json();
  },

  async mfaVerify(mfaToken: string, code: string, trustDevice = false, method?: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/mfa/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ mfa_token: mfaToken, code, trust_device: trustDevice, method }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "MFA verification failed" }));
      const e: any = new Error(err.detail || "MFA verification failed");
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    saveSession(data.access_token, data.user);
    saveCsrfToken(data?.csrf_token);
    return data;
  },

  // ── Passkeys (WebAuthn) ──────────────────────────────────────────────
  // Enrollment: authenticated (bearer + CSRF via request()).
  async passkeyRegisterOptions(): Promise<any> {
    return request("POST", "/auth/mfa/passkey/register/options", {});
  },
  async passkeyRegisterVerify(credential: any, nickname?: string): Promise<any> {
    return request("POST", "/auth/mfa/passkey/register/verify", { credential, nickname });
  },
  // Login flow: auth'd by the mfa_token (no session yet), CSRF-exempt — bare fetch.
  async passkeyAuthOptions(mfaToken: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/mfa/passkey/auth/options`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ mfa_token: mfaToken }),
    });
    if (!res.ok) {
      const e: any = new Error((await res.json().catch(() => ({}))).detail || "Couldn't start passkey sign-in");
      e.status = res.status; throw e;
    }
    return res.json();
  },
  async passkeyAuthVerify(mfaToken: string, credential: any, trustDevice = false): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/mfa/passkey/auth/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ mfa_token: mfaToken, credential, trust_device: trustDevice }),
    });
    if (!res.ok) {
      const e: any = new Error((await res.json().catch(() => ({}))).detail || "Passkey verification failed");
      e.status = res.status; throw e;
    }
    const data = await res.json();
    saveSession(data.access_token, data.user);
    saveCsrfToken(data?.csrf_token);
    return data;
  },

  // Passwordless login: usernameless passkey assertion, no password. The
  // WebAuthn ceremony IS the auth; on success the server mints a full session.
  // CSRF-exempt (no session cookie yet) — bare fetch.
  async passkeyLoginOptions(): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/passkey/login/options`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      const e: any = new Error((await res.json().catch(() => ({}))).detail || "Couldn't start passkey sign-in");
      e.status = res.status; throw e;
    }
    return res.json();
  },
  async passkeyLoginVerify(credential: any, trustDevice = false): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/passkey/login/verify`, {
      method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ credential, trust_device: trustDevice }),
    });
    if (!res.ok) {
      const e: any = new Error((await res.json().catch(() => ({}))).detail || "Passkey sign-in failed");
      e.status = res.status; throw e;
    }
    const data = await res.json();
    saveSession(data.access_token, data.user);
    saveCsrfToken(data?.csrf_token);
    return data;
  },

  // ── Manage 2FA (authenticated; request() auto-handles step-up) ──────────
  async mfaRemoveFactor(method: string): Promise<any> {
    return request("POST", "/auth/mfa/factor/remove", { method });
  },
  async mfaRegenerateRecovery(): Promise<any> {
    return request("POST", "/auth/mfa/recovery/regenerate", {});
  },
  async passkeyList(): Promise<any[]> {
    return request("GET", "/auth/mfa/passkey/list");
  },
  async passkeyRename(id: number, nickname: string): Promise<any> {
    return request("PATCH", `/auth/mfa/passkey/${id}`, { nickname });
  },
  async passkeyDelete(id: number): Promise<any> {
    return request("DELETE", `/auth/mfa/passkey/${id}`);
  },

  async logout(): Promise<void> {
    // Always clear local state, even if the backend call fails — better to
    // err on the side of "user thinks they're logged out" than leave them
    // in a half-state. But surface the failure to the console so a
    // misconfigured CSRF / network outage doesn't silently leave a live
    // server-side session (the historic root cause of "logout → reload →
    // logged back in"). Caller sees a resolved Promise either way; the
    // page typically navigates to the login screen on its own.
    try {
      await request("POST", "/auth/logout");
    } catch (e) {
      console.warn("[auth] /auth/logout failed; clearing local state anyway:", e);
    }
    clearSession();
  },

  async elevate(payload: { password?: string; totp_code?: string; recovery_code?: string; email_code?: string }): Promise<any> {
    const data = await request("POST", "/auth/elevate", payload);
    // CRITICAL: persist the NEW elevated access_token to sessionStorage.
    // We send sessionStorage's token as Authorization: Bearer on every
    // request, and auth_deps.get_current_user prefers the Bearer header
    // over the mp_access cookie. Without this, post-elevate requests
    // keep using the OLD non-elevated token from sessionStorage, and
    // require_elevated 403s in a loop. The cookie alone isn't enough
    // because of the Bearer-takes-priority order on the server.
    if (data?.access_token) {
      setToken(data.access_token);
    }
    return data;
  },

  async elevateEmailSend(): Promise<any> {
    return request("POST", "/auth/elevate/email/send", {});
  },

  async elevatePasskeyOptions(): Promise<any> {
    return request("POST", "/auth/mfa/passkey/elevate/options", {});
  },

  async elevatePasskeyVerify(credential: any): Promise<any> {
    const data = await request("POST", "/auth/mfa/passkey/elevate/verify", { credential });
    if (data?.access_token) {
      setToken(data.access_token);
    }
    return data;
  },

  async changePassword(currentPassword: string, newPassword: string, acknowledgeBreach = false): Promise<any> {
    // Caller is expected to have already elevated (via api.auth.elevate) so
    // require_elevated passes. The server still re-verifies currentPassword
    // inside the handler — defense in depth.
    return request("POST", "/auth/password-change", {
      current_password: currentPassword,
      new_password: newPassword,
      acknowledge_breach: acknowledgeBreach,
    });
  },

  async getSessions(): Promise<any[]> {
    return request("GET", "/auth/sessions");
  },

  async revokeSession(sessionId: number): Promise<void> {
    return request("DELETE", `/auth/sessions/${sessionId}`);
  },

  async revokeAllSessions(): Promise<any> {
    return request("POST", "/auth/sessions/revoke-all");
  },

  async me(): Promise<any> {
    return request("GET", "/auth/me");
  },

  // Password reset (public — no bearer needed)
  async requestPasswordReset(email: string): Promise<void> {
    // Always resolves without throwing on 204 — the server intentionally
    // returns the same 204 whether or not the account exists, to avoid
    // email enumeration. Network errors still throw.
    const res = await fetch(`${BASE_URL}/auth/password-reset/request`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (res.status >= 500) {
      throw new Error("Server error — please try again in a moment");
    }
    if (res.status === 429) {
      throw new Error("Too many attempts. Try again later.");
    }
    // 204, 400 (bad email), and any other 4xx all resolve as "we tried" —
    // we don't want to differentiate to the client. Only the 400 case
    // matters and we surface a generic success message regardless.
  },

  async confirmPasswordReset(token: string, password: string, acknowledgeBreach = false): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/password-reset/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, password, acknowledge_breach: acknowledgeBreach }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Reset failed" }));
      const e: any = new Error(typeof err.detail === "string" ? err.detail : (err.detail?.message || "Reset failed"));
      e.status = res.status;
      e.detail = err.detail;
      throw e;
    }
    return res.json();
  },

  // "That wasn't me" — public, token-authed (no bearer). GET is read-only
  // (safe for an email security gateway to prefetch); the actual action is
  // the POST, fired only by an explicit button click on the confirm page.
  async notMeStatus(token: string): Promise<{ email: string }> {
    const res = await fetch(`${BASE_URL}/auth/not-me/${encodeURIComponent(token)}/status`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Invalid or expired link" }));
      const e: any = new Error(typeof err.detail === "string" ? err.detail : "Invalid or expired link");
      e.status = res.status;
      throw e;
    }
    return res.json();
  },

  async notMeConfirm(token: string): Promise<any> {
    const res = await fetch(`${BASE_URL}/auth/not-me/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Couldn't complete this action" }));
      const e: any = new Error(typeof err.detail === "string" ? err.detail : "Couldn't complete this action");
      e.status = res.status;
      throw e;
    }
    return res.json();
  },
};


// ─── Binary download helper ──────────────────────────────────────────────────
// For endpoints that return a file (ZIP, CSV, etc) instead of JSON. Goes
// through rawRequest so 401-refresh-retry still works, but skips JSON parsing.
async function downloadBlob(path: string, method = "GET", body?: any): Promise<{ blob: Blob; filename: string }> {
  let res = await rawRequest(method, path, body);
  if (res.status === 401 && path !== "/auth/refresh") {
    const refreshed = await refreshAccessToken();
    if (refreshed) res = await rawRequest(method, path, body);
    else { notifySessionExpired(); throw new Error("Session expired. Please log in again."); }
  }
  if (res.status === 403) {
    const errBody = await res.clone().json().catch(() => ({} as any));
    if (errBody?.detail === "elevation_required") {
      try {
        const { promptStepUp } = await import("./components/StepUpModal");
        await promptStepUp();
        res = await rawRequest(method, path, body);
      } catch { /* user cancelled */ }
    }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: "Download failed" }));
    const e: any = new Error(err.detail || "Download failed");
    e.status = res.status;
    throw e;
  }
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : "download";
  return { blob, filename };
}


/** An authenticated request that hands back the raw `Response`.
 *
 * For endpoints whose body is not JSON — rendered HTML, PDF bytes — where the
 * caller wants the Response itself. Everything a call site used to hand-roll
 * is handled here: the base URL, `credentials:"include"`, the CSRF header on
 * mutating methods, and the transient-failure retry.
 *
 * Hand-rolling this is what the ban in semgrep.yml (mp-no-handrolled-api-fetch)
 * exists to stop, and the reason is not style. A hand-rolled fetch reads the
 * csrf_token cookie directly, and cross-origin (Vercel → Railway) that cookie
 * is invisible to `document.cookie`: no header goes out and CSRFMiddleware
 * rejects the request. Even reading it through `getCsrfToken()` is not enough
 * on a freshly-opened tab, where the cookie is unreadable AND sessionStorage
 * is empty — `rawRequest` is the only path that bounces off /auth/refresh to
 * mint one first. Every one of those failures is invisible in local dev, where
 * the SPA and the API share an origin and the cookie reads fine.
 *
 * Non-2xx is returned, not thrown: these callers want to branch on the status
 * themselves (a 204 means "nothing to draw", a 503 means "no Chromium here").
 * A 401 is retried once behind a token refresh, so a session that expires
 * mid-edit re-renders instead of silently going blank.
 */
export async function apiRaw(method: string, path: string, body?: any): Promise<Response> {
  let res = await rawRequest(method, path, body);
  if (res.status === 401 && path !== "/auth/refresh") {
    const refreshed = await refreshAccessToken();
    if (refreshed) res = await rawRequest(method, path, body);
    else notifySessionExpired();
  }
  return res;
}


// A category PATCH can promote or retire a learned rule
// (backend services/rule_confidence.py), and the server says so in
// `rule_notice`. Toasting it here rather than in each of the ~8 recategorize
// surfaces means none can forget to. Lazy import keeps Toast (React) out of
// this module's load path.
function announceRuleChange(path: string) {
  return (res: any) => {
    const msg = res && typeof res === "object" ? res.rule_notice : null;
    if (msg && /^\/transactions\/\d+$/.test(path)) {
      import("./components/Toast").then(m => m.toast.info(msg)).catch(() => {});
    }
    return res;
  };
}

// ─── Public API ───────────────────────────────────────────────────────────────
export const api = {
  // `opts.signal` cancels the read. Prefer hooks/useFetch.ts over passing it
  // by hand — it owns the signal's lifetime and drops late responses.
  get:    (path: string, opts?: RequestOptions) => request("GET", path, undefined, false, opts),
  post:   (path: string, body?: any) => request("POST", path, body),
  patch:  (path: string, body?: any) => request("PATCH", path, body).then(announceRuleChange(path)),
  put:    (path: string, body?: any) => request("PUT", path, body),
  delete: (path: string, body?: any) => request("DELETE", path, body),
  upload: (path: string, formData: FormData) => request("POST", path, formData, true),
  download: downloadBlob,
  auth,
};


/**
 * Restore session on page load. Verifies token is still valid (or refreshable).
 * Called from App.tsx on mount.
 */
export async function restoreSession(): Promise<any | null> {
  // We always attempt /auth/me, even without a saved access token.
  // The request layer will see the 401, attempt /auth/refresh using
  // the (httpOnly) refresh cookie, and retry. If the refresh cookie
  // is also dead, clearSession + null is the correct end state.
  try {
    const user = await api.get("/auth/me");
    // Seed CSRF eagerly on boot if missing. /auth/me is a GET so the
    // standard 403→refresh→retry path doesn't fire for it — and a tab
    // opened to an already-authenticated session would otherwise reach
    // its first state-changing request with no CSRF token and a hard
    // CSRF failure (no recovery if refresh cookie was eaten by 3rd-party
    // cookie blocking). Doing this on boot makes the first POST work
    // on the first attempt.
    if (!getCsrfToken()) {
      try { await refreshAccessToken(); } catch { /* swallow — best effort */ }
    }
    return user;
  } catch {
    clearSession();
    return null;
  }
}
